import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Form,
  InputGroup,
  Table,
  Badge,
  ListGroup,
} from "react-bootstrap";
import {
  UserCheck,
  DollarSign,
  ShieldAlert,
  History,
  RefreshCcw,
  CheckCircle,
  Search,
  User,
  X,
  CreditCard,
} from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";

// Layout Components
import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1"; // Ensure this matches your Auth context path

const CREDIT_API = "https://beaconsms.com/oldsite/api/credit_user_api.php";

const CreditUser = () => {
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.id === "71";
  const dropdownRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);

  // Close search dropdown when clicking outside the input area
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    setFetchingData(true);
    try {
      const [userRes, historyRes] = await Promise.all([
        axios.get(`${CREDIT_API}?adminId=71`),
        axios.get(`${CREDIT_API}?adminId=71&action=history`),
      ]);
      setUsers(Array.isArray(userRes.data) ? userRes.data : []);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (error) {
      console.error("Error loading credit data:", error);
    } finally {
      setFetchingData(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    document.title = "Credit User - beaconSMS";
    fetchData();
  }, [fetchData]);

  // Search logic: Filters by Username, Name, or Phone (Null-Safe)
  const filteredUsers = useMemo(() => {
    const term = userSearch.toLowerCase().trim();
    if (!term) return users.slice(0, 10); // Show first 10 by default

    return users
      .filter((u) => {
        const username = u.username ? String(u.username).toLowerCase() : "";
        const name = u.name ? String(u.name).toLowerCase() : "";
        const phone = u.phone ? String(u.phone) : "";
        return (
          username.includes(term) || name.includes(term) || phone.includes(term)
        );
      })
      .slice(0, 15);
  }, [users, userSearch]);

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    setUserSearch(`@${u.username}`); // Display selected username
    setShowDropdown(false);
  };

  const handleCredit = async (e) => {
    e.preventDefault();
    if (!selectedUser || !amount) return;

    Swal.fire({
      title: "Confirm Credit",
      text: `Process credit for ${selectedUser.username}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      confirmButtonText: "Yes, Credit Now",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await axios.post(CREDIT_API, {
            adminId: 71,
            userId: selectedUser.id,
            amount: parseFloat(amount),
          });

          if (response.data.status === "success") {
            Swal.fire("Success!", response.data.message, "success");
            setAmount("");
            setSelectedUser(null);
            setUserSearch("");
            fetchData(); // Refresh history and balances
          } else {
            Swal.fire("Failed", response.data.message, "error");
          }
        } catch (error) {
          Swal.fire(
            "Error",
            "Transaction failed. Check API connectivity.",
            "error",
          );
        } finally {
          setLoading(false);
        }
      }
    });
  };

  if (authLoading)
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
      </div>
    );

  if (!isAdmin) {
    return (
      <Container className='mt-5 text-center p-5 bg-white border rounded shadow-sm'>
        <ShieldAlert size={64} className='text-danger mb-3' />
        <h2 className='fw-bold'>Access Denied</h2>
        <p className='text-muted'>
          You do not have permission to access the administrative credit module.
        </p>
      </Container>
    );
  }

  return (
    <Container>
      <Header user={user} />
      <Row className='mt-3 px-md-3'>
        {/* Sidebar */}
        <Col md='3' className='mb-3 mb-md-0'>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>

        {/* Main Content */}
        <Col md='9'>
          <div className='bg-white p-4 border rounded shadow-sm'>
            <div className='d-flex justify-content-between align-items-center mb-4 border-bottom pb-3'>
              <div className='d-flex align-items-center'>
                <CreditCard className='text-primary me-2' size={24} />
                <h4 className='mb-0 fw-bold text-primary'>
                  Credit User Wallet
                </h4>
              </div>
              <Button
                variant='link'
                onClick={fetchData}
                className='text-decoration-none p-0'
              >
                <RefreshCcw
                  size={18}
                  className={
                    fetchingData ? "fa-spin text-primary" : "text-muted"
                  }
                />
              </Button>
            </div>

            {/* Credit Form */}
            <Card className='border-0 bg-light mb-5 shadow-sm'>
              <Card.Body className='p-4'>
                <Form onSubmit={handleCredit}>
                  <Form.Group
                    className='mb-3 position-relative'
                    ref={dropdownRef}
                  >
                    <Form.Label className='fw-bold'>
                      Find User (Username, Name, or Phone)
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className='bg-white'>
                        <Search size={18} className='text-muted' />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder='Start typing...'
                        value={userSearch}
                        onChange={(e) => {
                          setUserSearch(e.target.value);
                          setShowDropdown(true);
                          if (selectedUser) setSelectedUser(null);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        className={
                          selectedUser
                            ? "border-primary fw-bold text-primary"
                            : ""
                        }
                      />
                      {userSearch && (
                        <Button
                          variant='outline-secondary'
                          onClick={() => {
                            setUserSearch("");
                            setSelectedUser(null);
                          }}
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </InputGroup>

                    {/* Search Results Dropdown */}
                    {showDropdown && (
                      <ListGroup
                        className='position-absolute w-100 shadow-lg mt-1 overflow-auto'
                        style={{ zIndex: 1050, maxHeight: "280px" }}
                      >
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((u) => (
                            <ListGroup.Item
                              key={u.id}
                              action
                              onClick={() => handleSelectUser(u)}
                              className='d-flex justify-content-between align-items-center py-2 border-start-0 border-end-0'
                            >
                              <div>
                                <div className='fw-bold text-primary'>
                                  @{u.username || "no-username"}
                                </div>
                                <small className='text-muted'>
                                  {u.name || "Unnamed"} •{" "}
                                  {u.phone || "No Phone"}
                                </small>
                              </div>
                              <Badge
                                bg='primary-subtle'
                                className='text-primary border border-primary-subtle px-3'
                              >
                                {u.wallet} units
                              </Badge>
                            </ListGroup.Item>
                          ))
                        ) : (
                          <ListGroup.Item className='text-muted text-center py-3'>
                            No users found.
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                    )}
                  </Form.Group>

                  <Form.Group className='mb-4'>
                    <Form.Label className='fw-bold'>
                      Amount to Credit (Gross Units)
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className='bg-white'>
                        <DollarSign size={18} className='text-muted' />
                      </InputGroup.Text>
                      <Form.Control
                        type='number'
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder='0.00'
                        min='3'
                        required
                      />
                    </InputGroup>
                    <Form.Text className='text-muted small'>
                      A 2.5 unit SMS alert fee is deducted. Net credit will be{" "}
                      <strong>
                        {(parseFloat(amount) - 2.5 || 0).toFixed(2)} units
                      </strong>
                      .
                    </Form.Text>
                  </Form.Group>

                  <Button
                    variant='primary'
                    type='submit'
                    className='w-100 py-2 fw-bold'
                    disabled={loading || !selectedUser}
                  >
                    {loading ? (
                      <Spinner animation='border' size='sm' />
                    ) : (
                      "Confirm & Process Transaction"
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            {/* History Table */}
            <div className='d-flex align-items-center mb-3'>
              <History className='text-muted me-2' size={20} />
              <h5 className='mb-0 fw-bold'>Recent Manual Credits</h5>
            </div>

            <div className='table-responsive'>
              <Table hover className='small border align-middle'>
                <thead
                  className='bg-light text-uppercase'
                  style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                >
                  <tr>
                    <th>User</th>
                    <th>Transaction Details</th>
                    <th className='text-center'>Units</th>
                    <th className='text-end'>Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length > 0 ? (
                    history.map((h, i) => (
                      <tr key={i}>
                        <td className='fw-bold text-primary'>@{h.username}</td>
                        <td>{h.tx_memo}</td>
                        <td className='text-center'>
                          <Badge
                            bg='success-subtle'
                            className='text-success border border-success-subtle'
                          >
                            +{h.tx_amount}
                          </Badge>
                        </td>
                        <td
                          className='text-end text-muted'
                          style={{ fontSize: "11px" }}
                        >
                          {h.tx_created}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='4' className='text-center py-4 text-muted'>
                        No recent manual credits found in log.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default CreditUser;
